import { supabase } from '../../services/supabase'
import type { SupplierMitigationStatus, SupplierMitigationTask } from '../../types'
import { resolveMitigationDisplayLocation } from '../../utils/regionToCoordinates'
import {
  buildAmbiguousSuppliersMessage,
  buildAskForSupplierMessage,
  buildNoSupplierMessage,
  cleanSupplierNameForLookup,
  extractRiskDimensionKey,
  extractSupplierNameCandidate,
  formatDimensionCommentaryMarkdown,
  formatSupplierRiskRatingsMarkdown,
  isDimensionCommentaryAsk,
  isExplicitScorecardAsk,
  isSupplierScorecardAsk,
  type RiskRatingSupplier,
} from './supplierRiskRatingsTable'
import {
  resolveSupplierLegalName,
  SUPPLIER_PROFILE_SELECT,
  type SupplierRowWithId,
} from './resolveSupplierLegalName'
import { hideSimulatedSuppliers } from './simulatedSuppliers'
import {
  collapseToBestSupplierRowsByParent,
  supplierDisplayName,
} from './supplierRollup'
import type { Supplier } from '../../types'

export type { ResolveSupplierLegalNameResult } from './resolveSupplierLegalName'
export { resolveSupplierLegalName, shortSupplierAlias, isAliasUniqueForName } from './resolveSupplierLegalName'

export type ProfileMitigation = {
  task: string
  status: SupplierMitigationStatus
  date: string
  location: string
  brand: string
}

export type RiskRatingsDbAnswer =
  | {
      kind: 'text'
      content: string
      choices?: string[]
      resolvedSupplier?: string | null
      resolvedCategory?: string | null
    }
  | {
      kind: 'supplier_profile'
      content: string
      supplier: RiskRatingSupplier
      brands: string[]
      mitigations: ProfileMitigation[]
    }

/**
 * Resolve a named supplier into profile + brands + risk matrix + mitigations.
 * Indirect parents collapse to portfolio display grain (parent title + product lines).
 */
export async function resolveSupplierProfile(
  nameQuery: string
): Promise<RiskRatingsDbAnswer> {
  const chosenBundle = await resolveChosenSupplier(nameQuery)
  if (chosenBundle.kind === 'text') return chosenBundle

  const { chosen, rows } = chosenBundle
  const parent = (chosen.parent_name || '').trim()
  const isIndirectParent = chosen.procurement_type === 'indirect' && !!parent

  let matchingRows = rows
  let profileChosen: SupplierRowWithId = chosen

  if (isIndirectParent) {
    const groupRows = await fetchProfileRowsByParentName(parent)
    if (groupRows.length > 0) {
      const byId = new Map(groupRows.map((r) => [r.id, r]))
      for (const r of rows) byId.set(r.id, r)
      matchingRows = [...byId.values()]
      const collapsed = collapseToBestSupplierRowsByParent(matchingRows as Supplier[])
      const best = collapsed[0]
      if (best) {
        profileChosen = { ...best, id: best.id } as SupplierRowWithId
      }
    } else {
      const nameKey = (chosen.name || '').trim().toLowerCase()
      matchingRows = rows.filter((r) => (r.name || '').trim().toLowerCase() === nameKey)
    }
  } else {
    const nameKey = (chosen.name || '').trim().toLowerCase()
    matchingRows = rows.filter((r) => (r.name || '').trim().toLowerCase() === nameKey)
  }

  const brands = [
    ...new Set(matchingRows.map((r) => (r.brand || '').trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

  const supplierIds = matchingRows.map((r) => r.id).filter(Boolean)
  const mitigations = await fetchMitigationsForSupplierIds(
    supplierIds,
    profileChosen,
    matchingRows
  )

  const { id: _id, ...supplier } = profileChosen

  return {
    kind: 'supplier_profile',
    content: formatSupplierRiskRatingsMarkdown(supplier),
    supplier,
    brands,
    mitigations,
  }
}

async function fetchProfileRowsByParentName(parentName: string): Promise<SupplierRowWithId[]> {
  const parent = parentName.trim()
  if (!parent) return []
  const { data, error } = await supabase
    .from('suppliers')
    .select(SUPPLIER_PROFILE_SELECT)
    .eq('parent_name', parent)
    .order('name')
    .limit(200)
  if (error) {
    throw new Error(error.message || 'Failed to load parent supplier group.')
  }
  return hideSimulatedSuppliers((data ?? []) as unknown as SupplierRowWithId[])
}

/** Commentary for one or all risk dimensions from risk_dimension_narratives. */
export async function resolveSupplierDimensionCommentary(
  nameQuery: string,
  userText: string
): Promise<RiskRatingsDbAnswer> {
  const chosenBundle = await resolveChosenSupplier(nameQuery)
  if (chosenBundle.kind === 'text') return chosenBundle

  const { chosen } = chosenBundle
  const dimensionKey = extractRiskDimensionKey(userText)
  let profileChosen = chosen
  const parent = (chosen.parent_name || '').trim()
  if (chosen.procurement_type === 'indirect' && parent) {
    const groupRows = await fetchProfileRowsByParentName(parent)
    if (groupRows.length > 0) {
      const collapsed = collapseToBestSupplierRowsByParent(groupRows as Supplier[])
      const best = collapsed[0]
      if (best) profileChosen = { ...best, id: best.id } as SupplierRowWithId
    }
  }
  const { id: _id, ...supplier } = profileChosen
  return {
    kind: 'text',
    content: formatDimensionCommentaryMarkdown(supplier, dimensionKey),
    resolvedSupplier: supplierDisplayName(supplier) || null,
  }
}

/**
 * If the message asks for a ratings / supplier profile with a name, resolve from
 * `suppliers`. Returns null to fall through to normal chat.
 */
export async function tryAnswerRiskRatingsFromDb(
  userText: string
): Promise<RiskRatingsDbAnswer | null> {
  if (isDimensionCommentaryAsk(userText)) {
    const nameQuery = extractSupplierNameCandidate(userText)
    if (!nameQuery) {
      return {
        kind: 'text',
        content:
          'Which supplier’s risk commentary do you want? For example: “Strategic Risk commentary for Gissler & Pass” or “All commentaries for MM Packaging”.',
      }
    }
    return resolveSupplierDimensionCommentary(nameQuery, userText)
  }

  const nameQuery = extractSupplierNameCandidate(userText)

  if (!nameQuery) {
    if (isExplicitScorecardAsk(userText)) {
      return { kind: 'text', content: buildAskForSupplierMessage() }
    }
    return null
  }

  if (!isSupplierScorecardAsk(userText)) return null

  return resolveSupplierProfile(nameQuery)
}

type ChosenBundle =
  | { kind: 'text'; content: string; choices?: string[] }
  | { kind: 'ok'; chosen: SupplierRowWithId; rows: SupplierRowWithId[] }

async function resolveChosenSupplier(nameQuery: string): Promise<ChosenBundle> {
  let result = await resolveSupplierLegalName(nameQuery, supabase)
  if (result.status === 'none') {
    const cleaned = cleanSupplierNameForLookup(nameQuery)
    if (cleaned && cleaned.toLowerCase() !== nameQuery.trim().toLowerCase()) {
      result = await resolveSupplierLegalName(cleaned, supabase)
      if (result.status === 'none') {
        return { kind: 'text', content: buildNoSupplierMessage(cleaned) }
      }
    } else {
      const display =
        cleaned && !/^(tell|about|show|give|know)\b/i.test(cleaned) ? cleaned : nameQuery
      return { kind: 'text', content: buildNoSupplierMessage(display) }
    }
  }
  if (result.status === 'unique') {
    return { kind: 'ok', chosen: result.chosen, rows: result.rows }
  }
  if (result.status === 'ambiguous') {
    return {
      kind: 'text',
      content: buildAmbiguousSuppliersMessage(result.candidates),
      choices: result.candidates,
    }
  }
  return { kind: 'text', content: buildNoSupplierMessage(cleanSupplierNameForLookup(nameQuery) || nameQuery) }
}

async function fetchMitigationsForSupplierIds(
  supplierIds: string[],
  chosen: SupplierRowWithId,
  matchingRows: SupplierRowWithId[]
): Promise<ProfileMitigation[]> {
  if (supplierIds.length === 0) return []

  const { data, error } = await supabase
    .from('supplier_mitigation_tasks')
    .select('*')
    .in('supplier_id', supplierIds)
    .order('alert_date', { ascending: false })
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(error.message || 'Failed to load mitigations.')
  }

  const byId = new Map(matchingRows.map((r) => [r.id, r]))

  return ((data ?? []) as SupplierMitigationTask[]).map((t) => {
    const row = t.supplier_id ? byId.get(t.supplier_id) : undefined
    const supplierLike = row ?? chosen
    return {
      task: (t.task || '').trim() || '—',
      status: t.status,
      date: formatShortDate(t.alert_date || t.created_at),
      location: resolveMitigationDisplayLocation(t, {
        location: supplierLike.location ?? null,
        kenvue_region: supplierLike.kenvue_region ?? null,
      }) || '—',
      brand: (t.brand || row?.brand || '').trim() || '—',
    }
  })
}

function formatShortDate(raw: string | null | undefined): string {
  if (!raw) return '—'
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return String(raw)
  }
}
